import { defineStore } from 'pinia';
import { TCountryCode } from 'countries-list';
import { CheckoutEventNames, initializePaddle, Paddle } from '@paddle/paddle-js';
import Subscription, { SubscriptionPeriod } from '@/dashboard/types/subscription';
import { Workspace, WorkspaceUser } from '@/dashboard/types/workspace';
import { SubscriptionBase } from '@/dashboard/types/subscriptionBase';
import { PaymentApi } from '@/dashboard/api/payment';
import { Price } from '@/dashboard/types/price';
import { Transaction, TransactionUser } from '@/dashboard/types/transaction';
import paddle_constants from '@/dashboard/constants/paddle';
import { sendGoogleEvent } from '@/dashboard/helper';
import { Address } from '@/dashboard/types/address';
import { useAppStore } from '@/dashboard/store/appStore';
import { Discount } from '@/dashboard/types/discount';
import { IS_DEVELOPMENT } from '@/dashboard/constants/app';

export enum PaymentStep {
  Choose = 'choose',
  Address = 'address',
  Payment = 'payment',
  Success = 'success',
}

export enum PaymentDiscountStatus {
  default,
  checking,
  success,
  error,
}

type PurchaseStore = {
  paddle?: Paddle;
  step: PaymentStep;
  isOpenPopup: boolean;
  workspace?: Workspace;
  subscriptions: SubscriptionBase[];
  currentSubscription?: Subscription;
  selectedPlan?: SubscriptionBase;
  period: SubscriptionPeriod;
  prices: Price[];
  transaction?: Transaction;
  transactionUsers: TransactionUser[];
  name: string;
  countryCode: TCountryCode;
  zipCode: string;
  loading: boolean;
  address?: Address;
  discountCode: string;
  discountStatus: PaymentDiscountStatus;
  paymentError: string;
  discount: Discount | null;
};

export const usePurchaseStore = defineStore('purchaseStore', {
  state: (): PurchaseStore => ({
    paddle: undefined,
    step: PaymentStep.Choose,
    isOpenPopup: false,
    workspace: undefined,
    subscriptions: [],
    currentSubscription: undefined,
    selectedPlan: undefined,
    period: 'year',
    prices: [],
    transaction: undefined,
    transactionUsers: [],
    name: '',
    countryCode: 'US',
    zipCode: '',
    loading: false,
    discountCode: '',
    discountStatus: PaymentDiscountStatus.default,
    paymentError: '',
    discount: null,
  }),
  getters: {},
  actions: {
    async openPopup(workspace: Workspace, user: WorkspaceUser) {
      this.isOpenPopup = true;
      this.workspace = workspace;
      this.step = PaymentStep.Choose;
      this.name = user.name;
      this.countryCode = 'US';
      this.zipCode = '';
      this.loading = false;
      this.resetDiscount();

      this.subscriptions = await PaymentApi.getSubscriptions();
      this.prices = await PaymentApi.getPrices();

      if (workspace.subscription) {
        this.period = workspace.subscription.period;
        this.currentSubscription = workspace.subscription;
        this.selectedPlan = this.subscriptions.find((item: SubscriptionBase) => item.id === workspace.subscription?.price_id);
      } else {
        const defaultPrice = IS_DEVELOPMENT ? 'pri_01hdy9wc2wgm665b64mbmy7nrn' : 'pri_01hfvtbggbm6k8c1nsr21kds1q';
        this.currentSubscription = undefined;
        this.selectedPlan = this.subscriptions.find((item: SubscriptionBase) => item.id === defaultPrice);
        this.period = this.selectedPlan?.period ?? 'year';
      }

      if (!this.paddle) {
        await this.initPaddle();
      }
    },
    async initPaddle() {
      this.paddle = await initializePaddle({
        environment: paddle_constants.environment,
        token: paddle_constants.token,
        checkout: {
          settings: {
            allowLogout: false,
            showAddTaxId: false,
            theme: 'light',
          },
        },
        eventCallback: async (event) => {
          const appStore = useAppStore();
          if (event.data?.status === 'completed') {
            this.paddle?.Checkout.close();
            this.step = PaymentStep.Success;

            if (this.workspace?.id) {
              const workspaceId = this.workspace.id;
              await appStore.loadWorkspaceById(workspaceId);

              setTimeout(async () => {
                await appStore.loadWorkspaceById(workspaceId);
              }, 1000);
            }

            const subscriptionId = event.data?.items[0].price_id;
            sendGoogleEvent('new_subscription', { subscriptionId });
          }
        },
      });
    },
    closePopup() {
      this.isOpenPopup = false;
    },
    resetDiscount() {
      this.discountCode = '';
      this.discountStatus = PaymentDiscountStatus.default;
      this.paymentError = '';
      this.discount = null;
    },
    switchPeriod() {
      this.period = this.period === 'year' ? 'month' : 'year';
      this.selectedPlan = this.subscriptions.find(
        (price) => price.description.split(' ').pop() === this.selectedPlan?.description.split(' ').pop() && price.period === this.period
      );
    },
    selectPlan(plan: SubscriptionBase) {
      this.selectedPlan = plan;
    },
    setTransaction(transaction: Transaction) {
      this.transaction = transaction;
    },
    setTransactionUsers(users: TransactionUser[]) {
      this.transactionUsers = users;
    },
    setCountryCode(code: TCountryCode) {
      this.countryCode = code;
    },
    async getTransactionPreview(): Promise<boolean> {
      try {
        this.loading = true;
        if (this.selectedPlan && this.workspace) {
          const data = await PaymentApi.createTransactionPreview(
            this.selectedPlan?.id,
            this.workspace?.id,
            this.address?.id ?? '',
            this.discount?.id
          );
          this.transaction = data.transaction;
          this.transactionUsers = data.users;
          this.loading = false;
          return true;
        } else {
          this.loading = false;
          return false;
        }
      } catch (e: any) {
        this.loading = false;
        return false;
      }
    },
    async buy() {
      try {
        this.loading = true;
        if (this.selectedPlan && this.address && this.workspace && this.paddle) {
          const data = await PaymentApi.createTransaction(this.selectedPlan.id, this.workspace.id, this.address.id, this.discount?.id);
          const transaction = data.transaction;

          if (transaction.id) {
            this.paddle.Checkout.open({ transactionId: transaction.id });
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async createAddress(): Promise<Address | undefined> {
      try {
        this.loading = true;
        if (this.countryCode && this.zipCode && !this.address) {
          this.address = (await PaymentApi.createAddress(this.countryCode, this.zipCode)).address;
        }
      } catch (e) {
        this.loading = false;
      }

      this.loading = false;
      return this.address;
    },
    async cancelSubscription() {
      try {
        this.loading = true;
        const appStore = useAppStore();
        if (this.workspace) {
          const data = await PaymentApi.cancelSubscription(this.workspace.id);
          if (data.success) {
            await appStore.loadWorkspaceById(this.workspace.id);
            this.closePopup();
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async activateSubscription() {
      const appStore = useAppStore();

      try {
        this.loading = true;

        if (appStore.workspace) {
          const data = await PaymentApi.activateSubscription(appStore.workspace.id);
          if (data.success) {
            await appStore.loadWorkspaceById(appStore.workspace.id);
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async change() {
      const appStore = useAppStore();

      try {
        this.loading = true;

        if (this.selectedPlan && this.workspace) {
          const data = await PaymentApi.swapSubscription(this.selectedPlan?.id, this.workspace?.id);
          this.step = PaymentStep.Success;

          await appStore.loadWorkspaceById(data.workspace_id);
        }
      } finally {
        this.loading = false;
      }
    },
    async getDiscount() {
      if (this.selectedPlan) {
        try {
          const response = await PaymentApi.getDiscount(this.discountCode, this.selectedPlan.id);
          if (response.success) {
            this.discountStatus = PaymentDiscountStatus.success;
            this.paymentError = '';
            this.discount = response.discount;

            await this.getTransactionPreview();
          }
        } catch (e: any) {
          this.paymentError = e?.response?.data?.message ?? '';
          this.discountStatus = PaymentDiscountStatus.error;
        }
      }
    },
    changeDiscountCode(value: string) {
      if (this.discountStatus === PaymentDiscountStatus.error) {
        this.discountStatus = PaymentDiscountStatus.default;
      }
      this.discountCode = value;
    },
    goNext() {
      switch (this.step) {
        case PaymentStep.Choose:
          this.step = PaymentStep.Address;
          break;
        case PaymentStep.Address:
          this.step = PaymentStep.Payment;
          break;
        case PaymentStep.Payment:
          this.step = PaymentStep.Success;
          break;
        default:
          break;
      }
    },
    goPrev() {
      switch (this.step) {
        case PaymentStep.Address:
          this.step = PaymentStep.Choose;
          break;
        case PaymentStep.Payment:
          this.resetDiscount();
          this.step = PaymentStep.Address;
          break;
        case PaymentStep.Success:
          this.step = PaymentStep.Payment;
          break;
        default:
          break;
      }
    },
  },
});
